import React from "react";
import type {
  FieldDataLineageConfig,
  ForeignKeyConfig,
} from "@/components/shared/UniversalCRMTable/types/field-config-types";

// Simplified column configuration
export interface SimpleColumn {
  id: string;
  label: string;
  category: string;
  visible: boolean;
  width?: number;
  order?: number; // Column order for drag-and-drop
  priority: number;
  formOnly?: boolean; // Whether column is only for forms, not tables
  searchable?: boolean; // Whether column is searchable (defaults to true)
  // Per-row renderers are authored in entity-specific factories with a concrete
  // row type. The column list itself is heterogeneous, so `item` is erased to
  // `object` at this boundary; a method-style signature keeps the parameter
  // bivariant so each factory can declare its own concrete row type and callers
  // can pass any concrete row. `value` is the (dynamically-keyed) cell value.
  render?(value: unknown, item: object): React.ReactNode;
  mobilePriority?: number; // Priority for mobile layouts
  mobileType?: "primary" | "secondary" | "badge" | "hidden"; // Mobile layout type
  isForeignKey?: boolean;
  foreignKey?: ForeignKeyConfig;
  isReadOnlyDerived?: boolean;
  readOnlyReason?: string;
  dataLineage?: FieldDataLineageConfig;
}

// Mobile layout presets
export type MobileLayoutPreset = "compact" | "detailed" | "minimal" | "custom";

export interface MobileLayoutConfig {
  cardLayout: {
    primaryFields: number;
    secondaryFields: number;
    badgeFields: number;
    showActions: boolean;
  };
  drawerLayout: {
    groupBy: "category" | "type" | "none";
    showActions: boolean;
    actionLayout: "buttons" | "list" | "tabs";
  };
}

// Column manager state
export interface ColumnManagerState {
  columns: SimpleColumn[];
  visibleColumns: SimpleColumn[];
  hiddenColumns: SimpleColumn[];
  columnOrder: string[]; // Array of column IDs in display order
  isLoading: boolean;
  /**
   * Per-table preferences key (the provider's `storageKey`). Exposed so the live
   * SimpleTable state (`useSimpleTableState`) can back column WIDTH + PAGE SIZE
   * with the same per-user, per-table prefs namespace as visibility/order.
   */
  storageKey: string;
  mobileLayoutPreset: MobileLayoutPreset;
  mobileLayoutConfig: MobileLayoutConfig;
}

// Column manager actions
export interface ColumnManagerActions {
  toggleColumn: (columnId: string) => void;
  showAllColumns: () => void;
  hideAllColumns: () => void;
  resetToDefaults: () => void;
  toggleCategory: (category: string) => void;
  setColumns: (columns: SimpleColumn[]) => void;
  setColumnOrder: (order: string[]) => void;

  // Mobile layout intelligence
  getMobileCardLayout: (layoutType?: "compact" | "detailed") => {
    primaryFields: SimpleColumn[];
    secondaryFields: SimpleColumn[];
    badgeFields: SimpleColumn[];
  };
  getMobileDrawerSections: (groupBy?: "category" | "type" | "none") => {
    [sectionName: string]: SimpleColumn[];
  };
  setMobileLayoutPreset: (preset: MobileLayoutPreset) => void;
  getMobileLayoutPreset: () => MobileLayoutPreset;
}

export interface ColumnManagerContextType extends ColumnManagerState, ColumnManagerActions {
  // Additional utilities
  getColumn: (id: string) => SimpleColumn | undefined;
  getCategoryColumns: (category: string) => SimpleColumn[];
  getCategoryStats: (category: string) => { total: number; visible: number; hidden: number };
}

// Default column categories
export const DEFAULT_CATEGORIES = {
  basic: { label: "Basic Info", color: "bg-blue-100 text-blue-800" },
  status: { label: "Status & Priority", color: "bg-green-100 text-green-800" },
  assignment: { label: "Assignment", color: "bg-purple-100 text-purple-800" },
  contact: { label: "Contact", color: "bg-orange-100 text-orange-800" },
  dates: { label: "Dates", color: "bg-yellow-100 text-yellow-800" },
  metadata: { label: "Metadata", color: "bg-card-100 text-gray-800" },
  actions: { label: "Actions", color: "bg-red-100 text-red-800" },
  financial: { label: "Financial", color: "bg-emerald-100 text-emerald-800" },
  integration: { label: "Integration", color: "bg-indigo-100 text-indigo-800" },
  time: { label: "Time Tracking", color: "bg-cyan-100 text-cyan-800" },
  automation: { label: "Automation", color: "bg-pink-100 text-pink-800" },
} as const;
